import type { MenuProps } from '@bb/elements-web';
import {
  SemanticColor,
  Button,
  Color,
  Dropdown,
  Flex,
  Paragraph,
  Space,
  Text,
} from '@bb/elements-web';
import { Card, Tag } from 'antd';
import {
  ANNOUNCEMENT_USER_TYPE_MAP,
  type AnnouncementUser,
} from 'api/AnnouncementController';
import { Icon } from 'components';
import React from 'react';
import styled from 'styled-components';

type RecipientsSelectionFiltersProps = {
  uniqueCount: number;
  selectedGroups: Array<{ id: string; name: string; type?: string }>;
  selectedLocations: Array<{ index: number; name: string }>;
  userTypeFilters: AnnouncementUser[];
  onAddUserTypeFilter: (userType: AnnouncementUser) => void;
  onRemoveFilter: (
    filterType: 'group' | 'location' | 'userType',
    id: number | string | AnnouncementUser,
  ) => void;
};

const RecipientsSelectionFilters: React.FC<RecipientsSelectionFiltersProps> = ({
  uniqueCount,
  selectedGroups,
  selectedLocations,
  userTypeFilters,
  onAddUserTypeFilter,
  onRemoveFilter,
}) => {
  const menuItems: MenuProps['items'] = [
    {
      key: 'filter-global-admins-menu-item',
      label: (
        <span data-cy="filter-global-admins-menu-item">
          Remove Global Admins
        </span>
      ),
      onClick: () => onAddUserTypeFilter('Global Admins'),
    },
    {
      key: 'filter-branch-admins-menu-item',
      label: (
        <span data-cy="filter-branch-admins-menu-item">
          Remove Branch Admins
        </span>
      ),
      onClick: () => onAddUserTypeFilter('Branch Admins'),
    },
    {
      key: 'filter-team-admins-menu-item',
      label: (
        <span data-cy="filter-team-admins-menu-item">Remove Team Admins</span>
      ),
      onClick: () => onAddUserTypeFilter('Team Admins'),
    },
    {
      key: 'filter-agents-menu-item',
      label: <span data-cy="filter-agents-menu-item">Remove Agents</span>,
      onClick: () => onAddUserTypeFilter('Agents'),
    },
  ];

  return (
    <StyledRootContainer>
      <StyledHeaderContainer align="baseline" justify="space-between">
        <Paragraph>
          <Text thickness="bold">{uniqueCount}</Text> Total Recipient(s)
          Selected
        </Paragraph>
        <Dropdown menu={{ items: menuItems }}>
          <Button
            type="secondary"
            color="grey"
            dataCy="filters-dropdown-button"
            size="small"
            minWidth={10}
          >
            <Space align="center">
              <Icon type="antdFilterFilled" height={14} width={12} />
              Filters
              <Icon type="antdDownOutlined" height={12} width={12} />
            </Space>
          </Button>
        </Dropdown>
      </StyledHeaderContainer>

      <StyledCard data-cy="selected-filters-container">
        {selectedGroups.map(group => (
          <StyledTag
            key={`group-${group.id}`}
            color={
              group.type === 'team' ? SemanticColor.primary : SemanticColor.grey
            }
            closable
            onClose={() => onRemoveFilter('group', group.id)}
            onClick={() => onRemoveFilter('group', group.id)}
            data-cy={`selected-group-tag-${group.id}`}
          >
            {group.name}
          </StyledTag>
        ))}
        {selectedLocations.map(location => (
          <StyledTag
            key={`location-${location.index}`}
            color={Color.PRIMARY_LEGACY}
            closable
            onClose={() => onRemoveFilter('location', location.index)}
            onClick={() => onRemoveFilter('location', location.index)}
            data-cy={`selected-location-tag-${location.index}`}
          >
            {location.name}
          </StyledTag>
        ))}
        {userTypeFilters.map(userType => (
          <StyledTag
            key={`filter-${userType}`}
            color={SemanticColor.danger}
            closable
            onClose={() => onRemoveFilter('userType', userType)}
            onClick={() => onRemoveFilter('userType', userType)}
            data-cy={`selected-filtered-user-type-tag-${ANNOUNCEMENT_USER_TYPE_MAP[userType]}`}
            icon={
              <Icon
                type="antdFilterFilled"
                color="white"
                height={14}
                width={12}
              />
            }
          >
            No {userType}
          </StyledTag>
        ))}
      </StyledCard>
    </StyledRootContainer>
  );
};

const StyledRootContainer = styled.div`
  margin-bottom: 20px;
`;

const StyledHeaderContainer = styled(Flex)`
  margin-bottom: 0;

  &&& button[data-cy='filters-dropdown-button'] {
    padding: 2px 4px;
    font-size: unset;
  }
`;

const StyledCard = styled(Card)`
  background-color: ${Color.LIGHTER_GREY};

  && .ant-card-body {
    min-height: 8em;
    padding: 0.5em;
    display: flex;
    align-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;

const StyledTag = styled(Tag)`
  cursor: pointer;
  margin-bottom: 4px;
`;

export default RecipientsSelectionFilters;
